// FILE: ProductList/ProductListSkeletons.jsx
// Skeleton loading components for product listing

import React from "react";

// Shimmer animation component
const Shimmer = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

// Product Card Skeleton for Grid View
export const ProductCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    {/* Image placeholder */}
    <div className="relative">
      <Shimmer className="h-40 sm:h-48 w-full rounded-none" />
      {/* Badge placeholder */}
      <div className="absolute top-2 right-2">
        <Shimmer className="h-5 w-16 rounded-full" />
      </div>
    </div>

    <div className="p-3 lg:p-4">
      {/* Title & ID */}
      <div className="mb-2">
        <Shimmer className="h-4 w-3/4 mb-1.5" />
        <Shimmer className="h-3 w-1/2" />
      </div>

      {/* Type & Status */}
      <div className="flex justify-between items-center mb-2 lg:mb-3">
        <Shimmer className="h-3 w-24" />
        <Shimmer className="h-5 w-14 rounded-full" />
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-1 mb-2 lg:mb-3 bg-gray-50 rounded-lg p-2">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="text-center space-y-1">
            <Shimmer className="h-2 w-8 mx-auto" />
            <Shimmer className="h-3 w-6 mx-auto" />
          </div>
        ))}
      </div>

      {/* Price Info Button */}
      <Shimmer className="h-9 w-full mb-2 lg:mb-3" />

      {/* Actions */}
      <div className="flex gap-2">
        <Shimmer className="h-9 flex-1" />
        <Shimmer className="h-9 flex-1" />
        <Shimmer className="h-9 w-10" />
      </div>
    </div>
  </div>
);

// Product Row Skeleton for Desktop Table
export const ProductRowSkeleton = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4">
      <Shimmer className="h-4 w-4" />
    </td>
    <td className="px-6 py-4">
      <div className="flex items-center gap-3">
        <Shimmer className="w-10 h-10 rounded-lg" />
        <div className="space-y-1.5">
          <Shimmer className="h-4 w-32" />
          <Shimmer className="h-3 w-20" />
        </div>
      </div>
    </td>
    <td className="px-6 py-4">
      <div className="space-y-1.5">
        <Shimmer className="h-4 w-16" />
        <Shimmer className="h-3 w-12" />
      </div>
    </td>
    <td className="px-6 py-4">
      <div className="space-y-1.5">
        <Shimmer className="h-4 w-14" />
        <Shimmer className="h-3 w-12" />
      </div>
    </td>
    <td className="px-6 py-4">
      <div className="space-y-1.5">
        <Shimmer className="h-4 w-8" />
        <Shimmer className="h-3 w-6" />
      </div>
    </td>
    <td className="px-6 py-4">
      <div className="space-y-1.5">
        <Shimmer className="h-4 w-20" />
        <Shimmer className="h-3 w-10" />
      </div>
    </td>
    <td className="px-6 py-4">
      <Shimmer className="h-5 w-16 rounded-full" />
    </td>
    <td className="px-6 py-4">
      <div className="flex gap-2">
        <Shimmer className="h-7 w-7 rounded" />
        <Shimmer className="h-7 w-7 rounded" />
      </div>
    </td>
  </tr>
);

// Mobile Card Skeleton for List View
export const ProductMobileCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow p-3 animate-pulse">
    <div className="flex gap-3">
      {/* Checkbox & Image */}
      <div className="flex-shrink-0">
        <Shimmer className="w-5 h-5 mb-2" />
        <Shimmer className="w-16 h-16 rounded-lg" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start mb-1">
          <div className="min-w-0 flex-1 mr-2">
            <Shimmer className="h-4 w-3/4 mb-1" />
            <Shimmer className="h-3 w-1/2" />
          </div>
          <Shimmer className="h-5 w-14 rounded-full flex-shrink-0" />
        </div>

        <div className="flex items-center gap-2 mb-2">
          <Shimmer className="h-3 w-12" />
          <Shimmer className="h-3 w-16" />
          <Shimmer className="h-3 w-14" />
        </div>

        <div className="flex items-center justify-between">
          <Shimmer className="h-5 w-20" />
          <div className="flex gap-1">
            <Shimmer className="h-8 w-8 rounded-lg" />
            <Shimmer className="h-8 w-8 rounded-lg" />
            <Shimmer className="h-8 w-8 rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Grid View Skeleton Container
export const ProductGridSkeleton = ({ count = 8 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-4">
    {[...Array(count)].map((_, i) => (
      <ProductCardSkeleton key={i} />
    ))}
  </div>
);

// List View Skeleton Container
export const ProductListSkeleton = ({ count = 8 }) => (
  <>
    {/* Desktop Table Skeleton */}
    <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left">
              <div className="w-4 h-4 bg-gray-200 rounded animate-pulse" />
            </th>
            {["Product", "Type", "Weight", "Stock", "Price", "Status", "Actions"].map((header) => (
              <th key={header} className="px-6 py-3 text-left">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {header}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {[...Array(count)].map((_, i) => (
            <ProductRowSkeleton key={i} />
          ))}
        </tbody>
      </table>
    </div>

    {/* Mobile List Skeleton */}
    <div className="lg:hidden space-y-3">
      {[...Array(count)].map((_, i) => (
        <ProductMobileCardSkeleton key={i} />
      ))}
    </div>
  </>
);

// Card Overlay for individual operations (edit/delete in progress)
export const CardOperationOverlay = ({ type = "updating" }) => (
  <div className="absolute inset-0 bg-white/80 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-lg">
    <div className="flex flex-col items-center gap-2">
      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      <span className="text-xs text-gray-600 font-medium">
        {type === "deleting" ? "Deleting..." : "Updating..."}
      </span>
    </div>
  </div>
);

// Inline spinner for smaller operations
export const InlineSpinner = ({ size = 16 }) => (
  <div
    className="border-2 border-current border-t-transparent rounded-full animate-spin"
    style={{ width: size, height: size }}
  />
);

export default {
  ProductCardSkeleton,
  ProductRowSkeleton,
  ProductMobileCardSkeleton,
  ProductGridSkeleton,
  ProductListSkeleton,
  CardOperationOverlay,
  InlineSpinner,
};
